import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { User } from '../../../_models/User';
import { Engine } from '../../../_models/Engine';
import { Vendor, VendorUser } from '../../../_models/fundraising/Vendor';
import { DialogService } from '../../../_services/dialog.service';
import { VendorService } from '../../../_services/fundraising/vendor.service';
import { UserService } from '../../../_services/user.service';
import { EngineService } from '../../../_services/engine.service';
import { forkJoin } from 'rxjs';
import { ClientType } from '../../../_models/ClientType';

@Component({
  selector: 'app-vendor-create',
  templateUrl: './vendor-create.component.html',
  styleUrls: ['./vendor-create.component.css']
})
export class VendorCreateComponent implements OnInit {

  public vendorForm: FormGroup;
  public vendorId: number;

  public engines: Engine[] = [];
  public users: User[] = [];
  public clientTypes: ClientType[] = [];

  public enableSubscription = false;
  public validationErrors = '';

  constructor(
    private vendorService: VendorService,
    private userService: UserService,
    private engineService: EngineService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef
  ) { }

  ngOnInit(): void {

    this.spinner.show();

    const userServiceObs = this.userService.getAllUsers();
      const engineServiceObs = this.engineService.getEngines();
      const clientTypeObs = this.userService.getCUEFundraisingClientTypes();

      forkJoin([userServiceObs, engineServiceObs, clientTypeObs])
        .subscribe(data => {
          this.users = data[0];
          this.engines = data[1];
          this.clientTypes = data[2];

          this.route.params
            .pipe(mergeMap(params => {
              this.vendorId = params['id'];
              return this.vendorId > 0 ? this.vendorService.getById(this.vendorId) : of(null);
            })).subscribe(
              data => {
                this.createForm(data);
                this.spinner.hide();
              },
              error => {
                this.dialogService.alert("Unable to load vendor", error.error, this.viewRef);
                this.spinner.hide();
              }
            ); 
        },
        error => {
          this.dialogService.alert('Oops something went wrong..', error.error, this.viewRef);
        });

    

  }

  createForm(vu: VendorUser) {
    this.vendorForm = this.fb.group({
      vendorId: [vu?.vendor.vendorId],
      vendorName: [vu?.vendor.name, [Validators.required, Validators.maxLength(30)]],
      active: [vu?.vendor.active],
      enableSubscriptionPeriod: [false],

      showAddressFields: [false],
      addressLine1: [vu?.vendor.locations[0]?.address.addressLine1],
      addressLine2: [vu?.vendor.locations[0]?.address.addressLine2],
      buildingName: [vu?.vendor.locations[0]?.address.buildingName],
      city: [vu?.vendor.locations[0]?.address.city],
      stateProvince: [vu?.vendor.locations[0]?.address.stateProvince],
      country: [vu?.vendor.locations[0]?.address.country],
      postalCode: [vu?.vendor.locations[0]?.address.postalCode],

      primaryContactId : [vu?.user.userId || 0],
      primaryContactFirstName: [vu?.user.firstName, [Validators.required, Validators.maxLength(30)]],
      primaryContactLastName: [vu?.user.lastName, [Validators.required, Validators.maxLength(30)]],
      primaryContactClientTypeId: [vu?.user.clientType.clientTypeId || 0],
      email: [vu?.user.email, Validators.required],
      canProgramCustomTracks: { value: vu?.user.canProgramCustomTracks, disabled: vu?.user.userId > 0 },//[vu?.user.canProgramCustomTracks],
      enableNetworkSync: {value: vu?.user.enableNetworkSync, disabled: vu?.user.userId > 0 },
      canEditVenues: {value: vu?.user.canEditVenues, disabled: vu?.user.userId > 0 },
      phoneNumber: [vu?.user.phoneNumber, Validators.required],
      engineId: [vu?.user.engine.engineId || 0],
      period: { value: { startDate: new Date(), endDate: new Date(), isNew: false }, disabled: vu?.user.userId > 0  },
      enableBarrelPrinting: [vu?.vendor.enableBarrelPrinting],
      isMerchant: [vu?.vendor?.isMerchant || false],
      enableCue5050: [vu?.vendor.enableCue5050 || false, this.validateFundraisingProductSelection()],
      enablePledgePerWin: [vu?.vendor.enablePledgePerWin || false, this.validateFundraisingProductSelection()],
      pledgePerWinAdminPercent: [vu?.vendor.pledgePerWinAdminPercent || 0],
      bankStatementDescription: [vu?.vendor.bankStatementDescription, [Validators.minLength(5), Validators.maxLength(10), Validators.required]],
      stripeFeeResponsibility: [vu?.vendor.stripeFeeResponsibility || false]
    });

    if (this.vendorForm.controls.enablePledgePerWin) {

      this.vendorForm.controls.pledgePerWinAdminPercent.setValidators([Validators.min(0), Validators.max(100), Validators.required]);
    }

    this.setUserPeriod(vu?.user);
  }

  validateFundraisingProductSelection(): ValidatorFn{
    return (control: AbstractControl): { [key: string]: any } | null => {
      let isValid = true;
      if (this.vendorForm && control.value !== null) {
        if (!this.vendorForm.controls.enablePledgePerWin.value && !this.vendorForm.controls.enableCue5050.value) {
          isValid = false;
        }
      }
      return isValid
        ? null
        : { 'noTypeSelection': { value: control.value } };
    };
  }

  onChangeFundraisingProducts(event: any){

    if (event.target.checked){

      this.vendorForm.controls.enableCue5050.setErrors(null);
      this.vendorForm.controls.enablePledgePerWin.setErrors(null);
    } 

    if (this.vendorForm.controls.enablePledgePerWin) {

      this.vendorForm.controls.pledgePerWinAdminPercent.setValidators([Validators.min(0), Validators.max(100), Validators.required]);

    } else {

      this.vendorForm.controls.pledgePerWinAdminPercent.setValidators(null);
      this.vendorForm.controls.pledgePerWinAdminPercent.updateValueAndValidity();
    }
  }

  selectEngine() {
    if (this.vendorForm.controls.engineId.value > 0) {
      this.vendorForm.controls.primaryContactClientTypeId.setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.vendorForm.controls.primaryContactClientTypeId.setValidators(null);
      this.vendorForm.controls.primaryContactClientTypeId.patchValue(0);
    }

    this.vendorForm.updateValueAndValidity();
  }

  selectUser(userId) {
   const user = this.users.find(u => u.userId === +userId);
    if (user) {
      this.vendorForm.controls.primaryContactLastName.patchValue(user.lastName);
      this.vendorForm.controls.primaryContactFirstName.patchValue(user.firstName);
      this.vendorForm.controls.email.patchValue(user.email);
      this.vendorForm.controls.canProgramCustomTracks.patchValue(user.canProgramCustomTracks);
      this.vendorForm.controls.canEditVenues.patchValue(user.canEditVenues);
      this.vendorForm.controls.enableNetworkSync.patchValue(user.enableNetworkSync);
      this.vendorForm.controls.phoneNumber.patchValue(user.phoneNumber);
      this.vendorForm.controls.engineId.patchValue(user.engine.engineId);
      this.vendorForm.controls.primaryContactClientTypeId.patchValue(user.clientType.clientTypeId);

      this.vendorForm.controls.canProgramCustomTracks.disable();
      this.vendorForm.controls.canEditVenues.disable();
      this.vendorForm.controls.enableNetworkSync.disable();
      this.vendorForm.controls.primaryContactClientTypeId.disable();
      this.vendorForm.controls.email.disable();
      this.vendorForm.controls.engineId.disable();
      this.vendorForm.controls.enableSubscriptionPeriod.disable();
      this.setUserPeriod(user);
      this.vendorForm.updateValueAndValidity();
    } else {
      this.vendorForm.controls.primaryContactLastName.patchValue('');
      this.vendorForm.controls.primaryContactFirstName.patchValue('');
      this.vendorForm.controls.email.patchValue('');
      this.vendorForm.controls.canProgramCustomTracks.patchValue(false);
      this.vendorForm.controls.canEditVenues.patchValue(false);
      this.vendorForm.controls.enableNetworkSync.patchValue(false);
      this.vendorForm.controls.phoneNumber.patchValue('');
      this.vendorForm.controls.engineId.patchValue(0);
      this.vendorForm.controls.primaryContactClientTypeId.patchValue(0);

      this.vendorForm.controls.canProgramCustomTracks.enable();
      this.vendorForm.controls.canEditVenues.enable();
      this.vendorForm.controls.enableNetworkSync.enable();
      this.vendorForm.controls.primaryContactClientTypeId.enable();
      this.vendorForm.controls.email.enable();
      this.vendorForm.controls.engineId.enable();
      this.vendorForm.controls.period.enable();
      this.vendorForm.controls.enableSubscriptionPeriod.enable();
      this.enableSubscription = false;
      this.vendorForm.controls.enableSubscriptionPeriod.patchValue(false);
      this.vendorForm.updateValueAndValidity();
    }
  }

  setUserPeriod(user: User) {
    if (user && user.subscriptionStartDate && user.subscriptionEndDate) {
      this.vendorForm.controls.period.patchValue({
        startDate: new Date(user.subscriptionStartDate),
        endDate: new Date(user.subscriptionEndDate)
    });
      this.enableSubscription = true;
      this.vendorForm.controls.enableSubscriptionPeriod.patchValue(true);
      this.vendorForm.controls.period.disable();
    } else {
      this.enableSubscription = false;
      this.vendorForm.controls.enableSubscriptionPeriod.patchValue(false);
      this.vendorForm.controls.period.enable();
    }
  }

  save() {
    this.spinner.show();

    this.vendorForm.controls.primaryContactClientTypeId.enable();
    this.vendorForm.controls.email.enable();

    this.vendorForm.controls.primaryContactClientTypeId.updateValueAndValidity();
    this.vendorForm.controls.email.updateValueAndValidity();

    if (!this.vendorForm.valid) {
      this.vendorForm.markAllAsTouched();
      this.vendorForm.updateValueAndValidity();
      this.validationErrors = 'Please complete the required fields';

      if (this.vendorForm.controls.primaryContactId.value > 0) {
        this.vendorForm.controls.primaryContactClientTypeId.disable();
        this.vendorForm.controls.email.disable();
      }

      this.spinner.hide();
      return;
    }

    const user = this.users.find(u => u.email.replace(/\s/g, "").toLowerCase() === this.vendorForm.controls.email.value.replace(/\s/g, "").toLowerCase());
    if (user && this.vendorForm.controls.primaryContactId.value < 1) {
      this.validationErrors = 'A user with that email address has already been created';
      this.spinner.hide();
      return;
    }

    this.validationErrors = '';

    const vendorData = {
      vendorId: this.vendorId,
      vendorName: this.vendorForm.controls.vendorName.value,
      primaryContactId: this.vendorForm.controls.primaryContactId.value,
      address: {
        addressLine1: this.vendorForm.controls.addressLine1.value,
        addressLine2: this.vendorForm.controls.addressLine2.value,
        buildingName: this.vendorForm.controls.buildingName.value,
        city: this.vendorForm.controls.city.value,
        stateProvince: this.vendorForm.controls.stateProvince.value,
        country: this.vendorForm.controls.country.value,
        postalCode: this.vendorForm.controls.postalCode.value
      },
      primaryContactInfo: { 
        email: this.vendorForm.controls.email.value.replace(/\s/g, "").toLowerCase(),
        clientTypeId: this.vendorForm.controls.primaryContactClientTypeId.value,
        canProgramCustomTracks: this.vendorForm.controls.canProgramCustomTracks.value,
        phoneNumber : this.vendorForm.controls.phoneNumber.value,
        subscriptionStartDate: this.vendorForm.controls.period.value.startDate,
        subscriptionEndDate: this.vendorForm.controls.period.value.endDate, 
        engineId: this.vendorForm.controls.engineId.value,
        canEditVenues: this.vendorForm.controls.canEditVenues.value,  
        enableNetworkSync: this.vendorForm.controls.enableNetworkSync.value,
        firstName: this.vendorForm.controls.primaryContactFirstName.value,
        lastName: this.vendorForm.controls.primaryContactLastName.value
      },
      isMerchant: this.vendorForm.controls.isMerchant.value,
      enableCue5050: this.vendorForm.controls.enableCue5050.value,
      enablePledgePerWin: this.vendorForm.controls.enablePledgePerWin.value,
      pledgePerWinAdminPercent: this.vendorForm.controls.pledgePerWinAdminPercent.value,
      bankStatementDescription: this.vendorForm.controls.bankStatementDescription.value,
      stripeFeeResponsibility: this.vendorForm.controls.stripeFeeResponsibility.value,
    };

    if(this.vendorId > 0) {
      this.vendorService.update(vendorData).subscribe(
        () => { 
          this.spinner.hide();
          this.navigateUp();
        },
        error => {
          this.spinner.hide();
          this.dialogService.alert("Unable to update vendor", error.error, this.viewRef);
        }
      );
    } else {
      this.vendorService.create(vendorData).subscribe(
        () => { 
          this.spinner.hide();
          this.navigateUp();
        },
        error => {
          this.spinner.hide();
          this.dialogService.alert("Unable to create vendor", error.error, this.viewRef);
        }
      );
    }



  }

  public navigateUp() {
    this.router.navigate([this.vendorId > 0 ? '../../' : '../'], {relativeTo: this.route});
  }

}
